/*! *********************************************************************
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { AdobeIMS } from '@identity/imslib';
import { IEnvironment } from '@identity/imslib/adobe-id/IEnvironment';
import { imsEvents, IMS_EVENTS } from './ImsEvents';
var IMSDefaultConfig = {
    locale: 'en-US',
    scope: 'AdobeID,openid,read_organizations',
};
var Ims = /** @class */ (function () {
    /**
     * Constructor
     * @param options constructor options that is basicaly the `adobeid` configuration
     */
    function Ims(options) {
        var _this = this;
        /**
         * Current locale.
         * Default is `en_US`
         */
        this._locale = 'en_US';
        /**
         * State of debugging feature.
         * Default is false
         */
        this._debug = false;
        /**
         * Id of token interval.
         * Default is null
         */
        this._intervalForToken = null;
        /**
         * Interval in ms to check for token interval timer. 0 means no check.
         * Default is 0
         */
        this._intervalForTokenTime = 0;
        /**
         * Logs to console with prefix if debug option is set to true
         */
        this._log = function () {
            var _a;
            var data = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                data[_i] = arguments[_i];
            }
            if (_this._debug)
                (_a = console === null || console === void 0 ? void 0 : console.info) === null || _a === void 0 ? void 0 : _a.call.apply(_a, __spreadArray([console, 'IMS_DEBUG'], __read(data), false));
        };
        /**
         * Cleans options to construct adobeid object
         * (removes non adobeid properties)
         *
         * @param options options from initialization
         *
         * @returns options init cleaned to add to adobeid
         */
        this._getAdobeIdOptions = function (options) {
            var debug = options.debug, checkTokenTimer = options.checkTokenTimer, adobeid = __rest(options, ["debug", "checkTokenTimer"]);
            return adobeid;
        };
        this._adobeid = __assign(__assign(__assign({}, IMSDefaultConfig), this._getAdobeIdOptions(options)), { autoValidateToken: true, onAccessToken: function (tokenInformation) {
                var _a;
                _this._log('Calling onAccessToken', { token: tokenInformation.token });
                imsEvents.emit(IMS_EVENTS.TOKEN_AVAILABLE, {
                    token: tokenInformation.token,
                });
                (_a = options === null || options === void 0 ? void 0 : options.onAccessToken) === null || _a === void 0 ? void 0 : _a.call(options, tokenInformation);
                _this._clearIntervalForToken();
            }, onReauthAccessToken: function (reauthTokenInformation) {
                var _a;
                _this._log('Calling `onReauthAccessToken`', {
                    reauthTokenInformation: reauthTokenInformation,
                });
                imsEvents.emit(IMS_EVENTS.REAUTH_TOKEN, reauthTokenInformation);
                (_a = options === null || options === void 0 ? void 0 : options.onReauthAccessToken) === null || _a === void 0 ? void 0 : _a.call(options, reauthTokenInformation);
            }, onError: function (type, message) {
                var _a;
                _this._log('Calling `onError`', { type: type, message: message });
                imsEvents.emit(IMS_EVENTS.ERROR, { type: type, message: message });
                (_a = options === null || options === void 0 ? void 0 : options.onError) === null || _a === void 0 ? void 0 : _a.call(options, type, message);
            }, onAccessTokenHasExpired: function () {
                var _a;
                _this._log('Calling `onAccessTokenHasExpired`');
                imsEvents.emit(IMS_EVENTS.TOKEN_EXPIRED);
                (_a = options === null || options === void 0 ? void 0 : options.onAccessTokenHasExpired) === null || _a === void 0 ? void 0 : _a.call(options);
            }, onReady: function (appState) {
                var _a;
                _this._log('Calling `onReady`', { appState: appState });
                imsEvents.emit(IMS_EVENTS.READY, appState);
                (_a = options === null || options === void 0 ? void 0 : options.onReady) === null || _a === void 0 ? void 0 : _a.call(options, appState);
                _this._setIntervalForToken();
            } });
        this._intervalForTokenTime = (options === null || options === void 0 ? void 0 : options.checkTokenTimer) || 0;
        this._adobeIms = new AdobeIMS();
        this._adobeIms.initialize();
        if (options.debug && options.environment === IEnvironment.STAGE) {
            this._debug = true;
            this._adobeIms.enableLogging();
        }
    }
    /**
     * Clear interval to check if the user has been logged from another platform
     */
    Ims.prototype._clearIntervalForToken = function () {
        var _a;
        if (this._intervalForToken !== null) {
            this._log('Clearing interval to check if user is logged');
            (_a = window === null || window === void 0 ? void 0 : window.clearInterval) === null || _a === void 0 ? void 0 : _a.call(window, this._intervalForToken);
            this._intervalForToken = null;
        }
    };
    Object.defineProperty(Ims.prototype, "_adobeid", {
        /**
         * Update the global adobeid config
         */
        set: function (config) {
            if (window) {
                window.adobeid = __assign(__assign({}, window.adobeid), config);
            }
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Set an interval to check if user has been logged from another platform
     */
    Ims.prototype._setIntervalForToken = function () {
        var _this = this;
        var _a, _b, _c;
        (_b = (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.refreshToken) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (this._intervalForToken !== null || this._intervalForTokenTime === 0)
            return;
        this._log('Creating interval to check if user is logged', {
            refreshInterval: this._intervalForTokenTime,
        });
        this._intervalForToken = (_c = window === null || window === void 0 ? void 0 : window.setInterval) === null || _c === void 0 ? void 0 : _c.call(window, function () { return __awaiter(_this, void 0, void 0, function () {
            var error_1;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, ((_b = (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.refreshToken) === null || _b === void 0 ? void 0 : _b.call(_a))];
                    case 1:
                        _c.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _c.sent();
                        this._log({ error: error_1 });
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); }, this._intervalForTokenTime);
    };
    Object.defineProperty(Ims.prototype, "locale", {
        /**
         * Get the locale
         *
         * @returns the locale used
         */
        get: function () {
            return this._locale;
        },
        /**
         * Set the locale
         */
        set: function (locale) {
            this._locale = locale;
            this._adobeid = { locale: locale };
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Method used to redirect the user to signin url
     *
     * @param externalParameters
     *    object sent from outside in order to have the possibility to override the default values when the redirect uri is created
     */
    Ims.prototype.signIn = function (externalParameters) {
        var _a;
        return (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.signIn(externalParameters);
    };
    /**
     * Method used to redirect the user to the signup screen
     *
     * @param externalParameters
     *    object sent from outside in order to have the possibility to override the default values when the redirect uri is created
     */
    Ims.prototype.signUp = function (externalParameters) {
        var _a;
        (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.signUp(externalParameters);
    };
    /**
     * Method used for sign out the user
     *
     * @param externalParameters
     *    object sent from outside in order to have the possibility to override the default values when the redirect uri is created
     */
    Ims.prototype.signOut = function (externalParameters) {
        var _a;
        (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.signOut(externalParameters);
    };
    /**
     * Returns the access token value from the local storage
     *
     * @returns ITokenInformation or null
     */
    Ims.prototype.getAccessToken = function () {
        var _a, _b;
        var tokenInformation = (_b = (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.getAccessToken) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!tokenInformation)
            return null;
        return tokenInformation;
    };
    Object.defineProperty(Ims.prototype, "accessToken", {
        /**
         * Returns the access token value from the local storage
         *
         * @returns the access token or null
         */
        get: function () {
            var _a, _b;
            var tokenInformation = (_b = (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.getAccessToken) === null || _b === void 0 ? void 0 : _b.call(_a);
            if (!tokenInformation)
                return null;
            return tokenInformation.token;
        },
        enumerable: false,
        configurable: true
    });
    /**
     *
     * @returns
     *    a promise which is resolved as true in case the token is valid otherwise false validate the existing token;
     */
    Ims.prototype.validateToken = function () {
        var _a;
        return (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.validateToken();
    };
    /**
     * Refresh the existing token.
     *
     * @param externalParameters
     *    external parameters sent from outside of the library Note: if refresh token API fails, the triggerOnAccessTokenHasExpired will be triggered
     *
     */
    Ims.prototype.refreshToken = function (externalParameters) {
        var _a;
        return (_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.refreshToken(externalParameters);
    };
    /**
     * Get user profile
     * @returns a promise with user's profile or null
     */
    Ims.prototype.getProfile = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var profile;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, ((_a = this._adobeIms) === null || _a === void 0 ? void 0 : _a.getProfile())];
                    case 1:
                        profile = _b.sent();
                        if (profile) {
                            profile.avatarUrl = this._adobeIms.avatarUrl(profile.userId);
                        }
                        return [2 /*return*/, profile];
                }
            });
        });
    };
    return Ims;
}());
export default Ims;
